import React, { useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { Chalkboard } from '@phosphor-icons/react';
import { getStatusColor, getSentimentColor } from '@t-one-internal/design-system';

interface ILTRow {
    totalEvents: number;
    totalSessions: number;
    totalTrainers: number;
    totalLearners: number;
    attendanceStatus: string;
    statusCount: number;
}

interface OrgILTUtilizationWidgetProps {
    data?: ILTRow[] | null;
    loading?: boolean;
    error?: string;
}

const STATUS_CONFIG: Record<string, { label: string; color: string; dotClass: string }> = {
    attended: { label: 'Attended', color: getSentimentColor('positive'), dotClass: 'bg-emerald-500' },
    registered: { label: 'Registered', color: getStatusColor('in_progress'), dotClass: 'bg-blue-500' },
    absent: { label: 'No-Show', color: getSentimentColor('negative'), dotClass: 'bg-red-400' },
};

export const OrgILTUtilizationWidget: React.FC<OrgILTUtilizationWidgetProps> = ({ data, loading, error }) => {
    const rows = Array.isArray(data) ? data : [];

    const { stats, statusData } = useMemo(() => {
        const first = rows[0];
        const stats = first ? {
            events: Number(first.totalEvents) || 0,
            sessions: Number(first.totalSessions) || 0,
            trainers: Number(first.totalTrainers) || 0,
            learners: Number(first.totalLearners) || 0,
        } : { events: 0, sessions: 0, trainers: 0, learners: 0 };

        const statusData = rows.map(r => ({
            name: r.attendanceStatus,
            value: Number(r.statusCount) || 0,
            color: STATUS_CONFIG[r.attendanceStatus]?.color || '#9CA3AF',
        }));

        return { stats, statusData };
    }, [rows]);

    return (
        <div className="flex flex-col h-full p-5">
<div className="flex-1">
                {loading ? (
                    <div className="flex items-center justify-center h-full">
                        <div className="w-6 h-6 border-3 border-gray-200 border-t-primary rounded-full animate-spin" />
                    </div>
                ) : error ? (
                    <div className="flex items-center justify-center h-full text-red-500 text-sm">{error}</div>
                ) : rows.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
                        <Chalkboard className="w-8 h-8" />
                        <span className="text-sm">No ILT data</span>
                    </div>
                ) : (
                    <>
                        {/* Summary Stats */}
                        <div className="grid grid-cols-4 gap-3 mb-4">
                            <div className="text-center p-3 bg-primary/5 rounded-lg">
                                <div className="text-xl font-bold text-primary">{stats.events}</div>
                                <div className="text-[11px] text-gray-500">Events</div>
                            </div>
                            <div className="text-center p-3 bg-blue-50 rounded-lg">
                                <div className="text-xl font-bold text-blue-600">{stats.sessions}</div>
                                <div className="text-[11px] text-gray-500">Sessions</div>
                            </div>
                            <div className="text-center p-3 bg-emerald-50 rounded-lg">
                                <div className="text-xl font-bold text-emerald-600">{stats.trainers}</div>
                                <div className="text-[11px] text-gray-500">Trainers</div>
                            </div>
                            <div className="text-center p-3 bg-amber-50 rounded-lg">
                                <div className="text-xl font-bold text-amber-600">{stats.learners}</div>
                                <div className="text-[11px] text-gray-500">Learners</div>
                            </div>
                        </div>

                        {/* Donut + Legend */}
                        <div className="flex items-center gap-6">
                            <div style={{ width: 130, height: 130 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie data={statusData} cx="50%" cy="50%" innerRadius={35} outerRadius={60} dataKey="value" strokeWidth={0}>
                                            {statusData.map((entry, i) => (
                                                <Cell key={i} fill={entry.color} />
                                            ))}
                                        </Pie>
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="space-y-2">
                                {statusData.map(s => {
                                    const total = statusData.reduce((sum, x) => sum + x.value, 0);
                                    const pct = total > 0 ? Math.round((s.value / total) * 100) : 0;
                                    const config = STATUS_CONFIG[s.name];
                                    return (
                                        <div key={s.name} className="flex items-center gap-2">
                                            <div className={`w-3 h-3 rounded-full ${config?.dotClass || 'bg-gray-400'}`} />
                                            <div>
                                                <div className="text-sm font-medium text-gray-900">{pct}%</div>
                                                <div className="text-[11px] text-gray-400">{config?.label || s.name} ({s.value})</div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
